export interface FluencyPhrase {
  readonly id: string;
  readonly text: string;
}

export interface FluencyLanternChallenge {
  readonly id: string;
  readonly skillId: string;
  readonly phrases: readonly FluencyPhrase[];
  readonly reviewWords: readonly string[];
  readonly focusWord: string;
  readonly hints: readonly string[];
  readonly successMessage: string;
}

export const FLUENCY_LANTERN_RESTORATION_EVENT = 'fluency-lantern-path-restored';

export const FLUENCY_LANTERN_CHALLENGES: readonly FluencyLanternChallenge[] = [
  {
    id: 'rain-walk', skillId: 'reading-fluency-phrasing-1',
    phrases: [
      { id: 'after-rain', text: 'After the rain,' },
      { id: 'we-walk', text: 'we walk' },
      { id: 'bright-woods', text: 'through the bright woods.' },
    ],
    reviewWords: ['after', 'through', 'bright'], focusWord: 'through',
    hints: ['Start with the phrase that tells when.', 'Read each phrase smoothly, then take a gentle breath.', 'Find the word “through” in the final phrase.'],
    successMessage: 'Two smooth readings light the first pair of lanterns!',
  },
  {
    id: 'bird-home', skillId: 'reading-fluency-phrasing-2',
    phrases: [
      { id: 'every-bird', text: 'Every small bird' },
      { id: 'will-find', text: 'will find' },
      { id: 'warm-place', text: 'a warm place.' },
    ],
    reviewWords: ['small', 'every', 'place'], focusWord: 'every',
    hints: ['Begin with the phrase naming who the sentence is about.', 'Keep “will find” together as one smooth thought.', 'The review word “every” appears in the first phrase.'],
    successMessage: 'The next pair glows with calm, expressive reading!',
  },
  {
    id: 'helped-path', skillId: 'reading-fluency-phrasing-3',
    phrases: [
      { id: 'because-helped', text: 'Because we helped,' },
      { id: 'lantern-path', text: 'the lantern path' },
      { id: 'shines-again', text: 'shines again!' },
    ],
    reviewWords: ['again', 'because', 'helped'], focusWord: 'because',
    hints: ['The first phrase explains why something happened.', 'Let the comma be a gentle pause, not a race stop.', 'The review word “because” begins the sentence.'],
    successMessage: 'Every lantern shines. Your smooth reading restored the path!',
  },
];

export function validateFluencyLanternChallenges(challenges = FLUENCY_LANTERN_CHALLENGES) {
  if (challenges.length < 3) throw new RangeError('Fluency Lantern Path requires three challenges.');
  const ids = new Set<string>();
  challenges.forEach(challenge => {
    if (ids.has(challenge.id)) throw new RangeError(`Duplicate fluency challenge: ${challenge.id}`);
    ids.add(challenge.id);
    const phraseIds = challenge.phrases.map(phrase => phrase.id);
    if (phraseIds.length < 2 || new Set(phraseIds).size !== phraseIds.length) throw new RangeError(`Invalid phrase groups for ${challenge.id}.`);
    if (challenge.reviewWords.length < 3 || new Set(challenge.reviewWords).size !== challenge.reviewWords.length) throw new RangeError(`Invalid review words for ${challenge.id}.`);
    if (!challenge.reviewWords.includes(challenge.focusWord)) throw new RangeError(`Focus word missing from review set for ${challenge.id}.`);
    const passage = challenge.phrases.map(phrase => phrase.text.toLocaleLowerCase()).join(' ');
    if (!passage.includes(challenge.focusWord.toLocaleLowerCase())) throw new RangeError(`Focus word missing from passage for ${challenge.id}.`);
    if (challenge.hints.length < 3) throw new RangeError(`Progressive hints missing for ${challenge.id}.`);
  });
  return true;
}

validateFluencyLanternChallenges();
